/*
 * BandTrendBadge — shows when a school's overall risk band has MOVED between the
 * previous and current diagnostic period, e.g. "Caution → Healthy this period".
 * Helps boards see momentum at a glance. Renders nothing when there is no prior
 * period or the band is unchanged. Uses the shared 80/60 three-band logic.
 */
import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { band, BAND_LABELS } from "@/lib/riskBand";

const ORDER = { high: 0, caution: 1, healthy: 2 };

export const BandTrendBadge = ({ score, prevScore, testid, showThisPeriod = true, compact = false, className = "" }) => {
  const cur = band(score);
  const prev = band(prevScore);
  if (!cur || !prev || cur === prev) return null;

  const improved = ORDER[cur] > ORDER[prev];
  const Icon = improved ? TrendingUp : TrendingDown;
  const palette = improved
    ? { text: "#166534", bg: "#f0fdf4", line: "#bbf7d0" }
    : { text: "#991b1b", bg: "#fef2f2", line: "#fecaca" };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-semibold whitespace-nowrap ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"} ${className}`}
      style={{ color: palette.text, backgroundColor: palette.bg, borderColor: palette.line }}
      title={`Overall band moved from ${BAND_LABELS[prev]} to ${BAND_LABELS[cur]} since the previous period`}
      data-testid={testid}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {compact ? BAND_LABELS[cur] : `${BAND_LABELS[prev]} → ${BAND_LABELS[cur]}`}
      {showThisPeriod && !compact && <span className="font-medium opacity-80">this period</span>}
    </span>
  );
};
